import { useQuery } from '@tanstack/react-query';
import { getIdTokenJwt, getLoginId, getUserPhoneNumber } from '@/lib/auth';
import { UserRole } from '@/types/common.types';

export interface UserProfile {
  email: string;
  givenName: string;
  familyName: string;
  /** "Given Family", or empty if the token carries neither. */
  name: string;
  customerId: string;
  role: UserRole | string;
  loginId: string;
  phoneNumber: string;
}

/**
 * Everything the pages read off the ID token, in one query. Replaces the
 * scattered `await getIdToken()` calls in ngOnInit across three components.
 */
export function useUserProfile() {
  return useQuery({
    queryKey: ['user-profile'],
    staleTime: Infinity,
    queryFn: async (): Promise<UserProfile> => {
      const [jwt, loginId, phoneNumber] = await Promise.all([
        getIdTokenJwt(),
        getLoginId(),
        getUserPhoneNumber(),
      ]);
      const givenName = jwt.payload.given_name ?? '';
      const familyName = jwt.payload.family_name ?? '';
      return {
        email: jwt.payload.email,
        givenName,
        familyName,
        // The Angular version interpolated both directly, which rendered
        // "undefined undefined" for a user with no name attributes.
        name: [givenName, familyName].filter(Boolean).join(' '),
        customerId: jwt.payload['custom:tenant'],
        role: jwt.payload['custom:appRole'] ?? UserRole.USER,
        loginId,
        phoneNumber,
      };
    },
  });
}
